/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { factory as contains } from '@stdlib/array/base/assert/contains';
import isArray from '@stdlib/assert/is-array';
import isObject from '@stdlib/assert/is-object';
import { isPrimitive as isString } from '@stdlib/assert/is-string';
import hasOwnProp from '@stdlib/assert/has-own-property';
import format from '@stdlib/string/format';


// VARIABLES //

var METHODS = [ 'min', 'max', 'average', 'dense', 'ordinal' ];
var MISSING = [ 'last', 'first', 'remove' ];

var isMethod = contains( METHODS );
var isMissing = contains( MISSING );


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination for validated options
* @param {Object} options - function options
* @param {string} [options.method] - method determining how ties are treated
* @param {string} [opts.missing] - determines where missing values go (`first`,`last`, or `remove`)
* @param {Array} [opts.encoding] - array of values encoding missing values
* @returns {(null|Error)} null or an error
*/
function validate( opts, options ) {
	if ( !isObject( options ) ) {
		return new TypeError( format( 'invalid argument. Options argument must be an object. Value: `%s`.', options ) );
	}
	if ( hasOwnProp( options, 'encoding' ) ) {
		opts.encoding = options.encoding;
		if ( !isArray( opts.encoding ) ) {
			return new TypeError( format( 'invalid option. `%s` option must be an array. Option: `%s`.', 'encoding', opts.encoding ) );
		}
	}
	if ( hasOwnProp( options, 'method' ) ) {
		opts.method = options.method;
		if ( !isString( opts.method ) || !isMethod( opts.method ) ) {
			return new TypeError( format( 'invalid option. `%s` option must be one of the following: "%s". Option: `%s`.', 'method', METHODS.join( '", "' ), opts.method ) );
		}
	}
	if ( hasOwnProp( options, 'missing' ) ) {
		opts.missing = options.missing;
		if ( !isString( opts.missing ) || !isMissing( opts.missing ) ) {
			return new TypeError( format( 'invalid option. `%s` option must be one of the following: "%s". Option: `%s`.', 'missing', MISSING.join( '", "' ), opts.missing ) );
		}
	}
	return null;
}


// EXPORTS //

export default validate;
